import React from 'react';
import { useNavigate } from 'react-router-dom';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';
import { Package, ArrowRight, Calendar, Wrench, CheckCircle2 } from '../../lib/icons';

interface ChatActionProps {
  type: 'viewInventory' | 'viewInventoryItem' | 'viewMaintenancePlan' | 'createServiceRequest' | 'viewHoneyDoList';
  data?: {
    itemId?: string;
    itemName?: string;
  };
  onBottomSheetOpen?: (type: string, data?: any) => void;
}

const ChatAction: React.FC<ChatActionProps> = ({ type, data, onBottomSheetOpen }) => {
  const navigate = useNavigate();

  const handleAction = () => {
    // Use bottom sheets for certain actions
    if (type === 'viewHoneyDoList' && onBottomSheetOpen) {
      onBottomSheetOpen('honeyDoList');
      return;
    }

    // Fall back to navigation for other actions
    switch (type) {
      case 'viewInventory':
        navigate('/inventory');
        break;
      case 'viewInventoryItem':
        if (data?.itemId) {
          navigate('/inventory', { state: { selectedItemId: data.itemId } });
        }
        break;
      case 'viewMaintenancePlan':
        navigate('/maintenance-plan');
        break;
      case 'createServiceRequest':
        navigate('/service-requests', { state: { openNewRequest: true } });
        break;
    }
  };

  const renderContent = () => {
    switch (type) {
      case 'viewInventory':
        return (
          <>
            <Package size={16} color="#1f2937" />
            <Text style={styles.actionText}>View Inventory</Text>
            <ArrowRight size={16} color="#1f2937" />
          </>
        );
      case 'viewInventoryItem':
        return (
          <>
            <Package size={16} color="#1f2937" />
            <Text style={styles.actionText}>View {data?.itemName || 'Item'}</Text>
            <ArrowRight size={16} color="#1f2937" />
          </>
        );
      case 'viewMaintenancePlan':
        return (
          <>
            <Calendar size={16} color="#1f2937" />
            <Text style={styles.actionText}>View Maintenance Plan</Text>
            <ArrowRight size={16} color="#1f2937" />
          </>
        );
      case 'createServiceRequest':
        return (
          <>
            <Wrench size={16} color="#1f2937" />
            <Text style={styles.actionText}>Create Service Request</Text>
            <ArrowRight size={16} color="#1f2937" />
          </>
        );
      case 'viewHoneyDoList':
        return (
          <>
            <CheckCircle2 size={16} color="#1f2937" />
            <Text style={styles.actionText}>View HoneyDone List</Text>
            <ArrowRight size={16} color="#1f2937" />
          </>
        );
      default:
        return null;
    }
  };

  return (
    <Pressable
      onPress={handleAction}
      style={({ pressed }) => [
        styles.button,
        pressed && styles.buttonPressed
      ]}
    >
      <View style={styles.content}>
        {renderContent()}
      </View>
    </Pressable>
  );
};

const styles = StyleSheet.create({
  button: {
    marginTop: 8,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  buttonPressed: {
    backgroundColor: '#e5e7eb',
  },
  content: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
  },
  actionText: {
    fontSize: 14,
    color: '#1f2937',
  },
});

export default ChatAction;
